/**
 * Organization Context
 * Manages current organization state and provides org-scoped data access
 * Phase 3: Multi-Tenancy
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth-context';
import type { Database } from './supabase';

type Organization = Database['public']['Tables']['organizations']['Row'];
type OrganizationMember = Database['public']['Tables']['organization_members']['Row'];

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  currentMembership: OrganizationMember | null;
  isOwner: boolean;
  isAdmin: boolean;
  loading: boolean;
  switchOrganization: (orgId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentMembership, setCurrentMembership] = useState<OrganizationMember | null>(null);
  const [loading, setLoading] = useState(true);

  const isOwner = currentMembership?.role === 'owner';
  const isAdmin = currentMembership?.role === 'owner' || currentMembership?.role === 'admin';

  const fetchOrganizations = async () => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrganization(null);
      setCurrentMembership(null);
      setLoading(false);
      return;
    }

    try {
      // Fetch all organization memberships for the user
      const { data: memberships, error: membershipsError } = await supabase
        .from('organization_members')
        .select('*')
        .eq('user_id', user.id);

      if (membershipsError) throw membershipsError;

      if (!memberships || memberships.length === 0) {
        setOrganizations([]);
        setCurrentOrganization(null);
        setCurrentMembership(null);
        setLoading(false);
        return;
      }

      // Fetch organization details separately
      const orgIds = memberships.map(m => m.organization_id);
      const { data: orgs, error: orgsError } = await supabase
        .from('organizations')
        .select('*')
        .in('id', orgIds);

      if (orgsError) throw orgsError;

      setOrganizations(orgs || []);

      // Set current organization (first one, or from localStorage)
      const savedOrgId = localStorage.getItem('currentOrganizationId');
      let currentOrg = orgs?.find((org: Organization) => org.id === savedOrgId) || orgs?.[0] || null;

      setCurrentOrganization(currentOrg);

      // Set current membership
      if (currentOrg) {
        const membership = memberships?.find((m: any) => m.organization_id === currentOrg.id);
        setCurrentMembership(membership || null);
        localStorage.setItem('currentOrganizationId', currentOrg.id);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchOrganization = async (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (!org) return;

    setCurrentOrganization(org);
    localStorage.setItem('currentOrganizationId', orgId);

    // Fetch membership for new org
    const { data: membership } = await supabase
      .from('organization_members')
      .select('*')
      .eq('organization_id', orgId)
      .eq('user_id', user!.id)
      .single();

    setCurrentMembership(membership);
  };

  const refreshOrganizations = async () => {
    await fetchOrganizations();
  };

  useEffect(() => {
    fetchOrganizations();
  }, [user]);

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        currentMembership,
        isOwner,
        isAdmin,
        loading,
        switchOrganization,
        refreshOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
